import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';

type Theme = 'light' | 'dark';

const MANUAL_OVERRIDE_KEY = 'sigma-theme-manual';

interface ThemeContextValue {
  theme: Theme;
  toggle: () => void;
  setFromTeams: (teamsTheme: string) => void;
  /** Fuerza el modo oscuro mientras una vista lo pida, sin tocar el toggle del usuario.
   *  Usar vía el hook `useForceDarkMode`, no directamente. */
  setForceDark: (key: string, active: boolean) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    try {
      return localStorage.getItem('sigma-theme') === 'dark' ? 'dark' : 'light';
    } catch {
      return 'light';
    }
  });

  // Vistas que exigen fondo oscuro mientras están montadas (Brillamos). Mientras el conjunto
  // esté vacío —hoy, porque ninguna ruta del portal lo pide— el comportamiento del tema es
  // exactamente el de antes: manda el toggle del usuario.
  const [forcedDarkKeys, setForcedDarkKeys] = useState<readonly string[]>([]);

  // Una vez que el usuario elige el tema a mano (el toggle del Sidebar), esa elección debe ganar
  // sobre cualquier sync automático posterior de Teams. Sin esto, cruzar de zona (Reconocimiento
  // <-> Pulso, cada una con su propio efecto de sync en su layout) remonta el layout de destino,
  // vuelve a correr `setFromTeams` y pisa la elección manual con el tema real de Teams — bug
  // reportado 2026-09-09 ("se quita el modo oscuro al entrar a Fundamentales"). Se persiste en
  // localStorage (no solo en memoria) para que sobreviva también a un refresh completo de página,
  // no solo a la navegación SPA. Ref, no state: no debe disparar un nuevo `setFromTeams` con
  // identidad distinta ni forzar un re-render propio.
  const hasManualOverrideRef = useRef<boolean | null>(null);
  if (hasManualOverrideRef.current === null) {
    try {
      hasManualOverrideRef.current = localStorage.getItem(MANUAL_OVERRIDE_KEY) === 'true';
    } catch {
      hasManualOverrideRef.current = false;
    }
  }

  useEffect(() => {
    document.body.classList.toggle('dark', theme === 'dark' || forcedDarkKeys.length > 0);
    try { localStorage.setItem('sigma-theme', theme); } catch { /* storage unavailable */ }
  }, [theme, forcedDarkKeys]);

  const toggle = useCallback(() => {
    hasManualOverrideRef.current = true;
    try { localStorage.setItem(MANUAL_OVERRIDE_KEY, 'true'); } catch { /* storage unavailable */ }
    setTheme(t => (t === 'light' ? 'dark' : 'light'));
  }, []);

  const setFromTeams = useCallback((teamsTheme: string) => {
    if (hasManualOverrideRef.current) return;
    setTheme(teamsTheme === 'dark' ? 'dark' : 'light');
  }, []);

  const setForceDark = useCallback((key: string, active: boolean) => {
    setForcedDarkKeys(keys => {
      const has = keys.includes(key);
      if (active === has) return keys;
      return active ? [...keys, key] : keys.filter(k => k !== key);
    });
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, toggle, setFromTeams, setForceDark }}>
      {children}
    </ThemeContext.Provider>
  );
}

/** Mantiene el modo oscuro forzado mientras el componente que lo pide siga montado. */
export function useForceDarkMode(active: boolean, key = 'default'): void {
  const { setForceDark } = useThemeContext();
  const keyRef = useRef(key);
  keyRef.current = key;

  useEffect(() => {
    setForceDark(keyRef.current, active);
    return () => setForceDark(keyRef.current, false);
  }, [active, setForceDark]);
}

export function useThemeContext(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useThemeContext must be used inside ThemeProvider');
  return ctx;
}
